import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { planHasFeature } from '@shared/constants/index.ts';
import { companyApi } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import { EmptyState } from '../../components/ui.tsx';
import { useJobChatSettingsAccess } from '../../components/settings/job-chat-access.ts';
import { twilioSetupNeedsAttention } from '../../components/settings/twilio-setup-status.tsx';
import { SettingsHubTile, SettingsLeafPage } from '../../components/settings/settings-leaf-page.tsx';

/**
 * Settings › Texting & calls: where `#settings-messaging` lands now. The
 * compliance-heavy texting setup (Twilio, the managed number, A2P/Trust Hub
 * registration), calls, and job chat used to share one Advanced section; each
 * is a page of its own, and this one points at them.
 *
 * Each tile keeps the gate its cards had: texting and calls are for
 * `company:write`, calls only where this installation answers calls, and job
 * chat for whoever its own capability check lets in.
 */
export default function MessagingHubPage() {
  const { t } = useTranslation();
  const { company, can } = useSession();
  const jobChat = useJobChatSettingsAccess();
  const canEdit = can('company:write');
  // Same key and gate as the texting cards, so opening Texting setup from here
  // costs nothing more.
  const smsPlan = company !== null && planHasFeature(company.effectivePlan, 'sms');
  const sms = useQuery({
    queryKey: ['sms-settings'],
    queryFn: () => companyApi.smsSettings(),
    enabled: canEdit && smsPlan,
  });
  const textingNeedsAttention = twilioSetupNeedsAttention(sms.data?.twilioSetup);

  const tiles = [
    ...(canEdit ? [{ id: 'texting', to: '/settings/texting' }] : []),
    ...(canEdit && company?.voiceInboxAvailable ? [{ id: 'calls', to: '/settings/calls' }] : []),
    ...(jobChat.available ? [{ id: 'jobChat', to: '/settings/job-chat' }] : []),
  ];

  return (
    <SettingsLeafPage
      title={t('settings.messagingPages.hubTitle')}
      description={t('settings.messagingPages.hubDescription')}
      back={{ to: '/settings', label: t('settings.messagingPages.backToSettings') }}
    >
      {tiles.length === 0 && !jobChat.loading ? (
        <EmptyState title={t('settings.messagingPages.noAccess')} />
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2">
          {tiles.map((tile) => (
            <SettingsHubTile
              key={tile.id}
              to={tile.to}
              title={t(`settings.messagingPages.tiles.${tile.id}.title`)}
              description={t(`settings.messagingPages.tiles.${tile.id}.description`)}
              badge={
                tile.id === 'texting' && textingNeedsAttention ? t('settings.messagingPages.needsAttention') : null
              }
            />
          ))}
        </ul>
      )}
    </SettingsLeafPage>
  );
}
